import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Chip, Paper, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, TextField, InputAdornment, IconButton, CircularProgress, Stack, Tooltip, Badge,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import InsightsIcon from '@mui/icons-material/Insights';
import FilterListIcon from '@mui/icons-material/FilterList';
import { getSoldProducts, getSalesTotals } from '../../databaseManager';
import { channelLabel, displayDateTime, formatCurrency, parseStoredDate, storedDateDay } from '../products/productUtils';
import { SALE_PLATFORMS } from '../products/MarkAsSoldDialog';
import SaleHistoryDialog from './SaleHistoryDialog';
import FilterDialog, { countActiveFilters, inNumberRange, inDayRange, containsText } from '../filters/FilterDialog';

const EMPTY_FILTERS = {
  platform: [],
  brand: '',
  category: '',
  soldStart: '',
  soldEnd: '',
  priceMin: '',
  priceMax: '',
  unitsMin: '',
  unitsMax: '',
  tagsNotes: '',
};

const uniqueSpecValues = (products, key) => (
  [...new Set(products.map((p) => p.specs?.[key]).filter(Boolean))].sort((a, b) => a.localeCompare(b))
);

const PAGE_SIZE = 50;

/**
 * Sales: every product that has sold at least once, newest sale first (web
 * port of the iOS Sales tab). Rows come from each product's most-recent-sale
 * fields; clicking one opens its full saleHistory.
 */
export default function SalesPage() {
  const { customer } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [historyProduct, setHistoryProduct] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sold, t] = await Promise.all([
        getSoldProducts(customer),
        getSalesTotals(customer).catch((err) => { console.error('Sales totals failed:', err); return null; }),
      ]);
      setProducts(sold);
      setTotals(t);
    } catch (err) {
      console.error('Failed to load sales:', err);
    } finally {
      setLoading(false);
    }
  }, [customer]);

  useEffect(() => { load(); }, [load]);

  const filterFields = useMemo(() => [
    { type: 'multiselect', key: 'platform', label: 'Sold on', anyLabel: 'Any platform',
      options: SALE_PLATFORMS.map((p) => ({ value: p.value, label: p.label })) },
    { type: 'dateRange', label: 'Last sale date', startKey: 'soldStart', endKey: 'soldEnd' },
    { type: 'autocomplete', key: 'brand', label: 'Brand', placeholder: 'Select brand', options: uniqueSpecValues(products, 'Brand') },
    { type: 'autocomplete', key: 'category', label: 'Category', placeholder: 'Select category', options: uniqueSpecValues(products, 'Category') },
    { type: 'range', label: 'Last sale price', minKey: 'priceMin', maxKey: 'priceMax', prefix: '$' },
    { type: 'range', label: 'Units sold', minKey: 'unitsMin', maxKey: 'unitsMax' },
    { type: 'text', key: 'tagsNotes', label: 'Tags / Notes', placeholder: 'Search tags or notes…' },
  ], [products]);

  const activeFilterCount = useMemo(() => countActiveFilters(filters), [filters]);

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return products
      .filter((p) => {
        if (filters.platform.length > 0 && !filters.platform.includes(p.lastSaleChannel || 'off_platform')) return false;
        if (!inDayRange(storedDateDay(p.lastSaleAt), filters.soldStart, filters.soldEnd)) return false;
        if (filters.brand && !containsText(p.specs?.Brand, filters.brand)) return false;
        if (filters.category && !containsText(p.specs?.Category, filters.category)) return false;
        if (!inNumberRange(p.lastSalePrice, filters.priceMin, filters.priceMax)) return false;
        if (!inNumberRange(p.totalSold ?? 0, filters.unitsMin, filters.unitsMax)) return false;
        if (filters.tagsNotes) {
          const t = filters.tagsNotes;
          if (!(p.tags || []).some((tag) => containsText(tag, t)) && !containsText(p.notes, t)) return false;
        }
        if (!needle) return true;
        if ((p.specs?.Title || p.validatedTitle || '').toLowerCase().includes(needle)) return true;
        if ((p.sku || '').toLowerCase().includes(needle)) return true;
        return Object.values(p.specs || {}).some((v) => String(v).toLowerCase().includes(needle));
      })
      .sort((a, b) => (parseStoredDate(b.lastSaleAt)?.getTime() || 0) - (parseStoredDate(a.lastSaleAt)?.getTime() || 0));
  }, [products, search, filters]);

  const hasCriteria = !!(search.trim() || activeFilterCount > 0);
  const clearAll = () => { setSearch(''); setFilters(EMPTY_FILTERS); };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Sales</Typography>
        {totals && (
          <Chip
            size="small"
            label={`${totals.unitsSold} sold · ${totals.productsSold} item${totals.productsSold === 1 ? '' : 's'}`}
            sx={{ bgcolor: 'grey.100', fontWeight: 600 }}
          />
        )}
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          variant="outlined"
          startIcon={<InsightsIcon />}
          onClick={() => navigate(`/sales/${customer}/metrics`)}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Metrics
        </Button>
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
        <TextField
          size="small"
          placeholder="Search sales…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: { sm: 260 } }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} /></InputAdornment>,
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch('')}><ClearIcon fontSize="small" /></IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
        <Tooltip title="Filters">
          <IconButton size="small" onClick={() => setFilterDialogOpen(true)} color={activeFilterCount > 0 ? 'primary' : 'default'}>
            <Badge badgeContent={activeFilterCount || null} color="primary">
              <FilterListIcon fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>
        {hasCriteria && <Button size="small" onClick={clearAll} sx={{ textTransform: 'none' }}>Clear all</Button>}
      </Stack>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 64 }} />
                  <TableCell>Item</TableCell>
                  <TableCell>Sold on</TableCell>
                  <TableCell>Last sale</TableCell>
                  <TableCell align="right">Sale price</TableCell>
                  <TableCell align="right">Units sold</TableCell>
                  <TableCell align="right">Revenue</TableCell>
                  <TableCell align="right">Profit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.slice(0, limit).map((p) => {
                  const image = p.thumbnailUrls?.[0] || p.mainImage;
                  const profit = p.totalRevenue != null && p.totalCost != null ? p.totalRevenue - p.totalCost : null;
                  return (
                    <TableRow key={p.productUri} hover sx={{ cursor: 'pointer' }} onClick={() => setHistoryProduct(p)}>
                      <TableCell sx={{ p: 0.75 }}>
                        {image
                          ? <Box component="img" src={image} alt="" sx={{ width: 48, height: 48, borderRadius: 1, objectFit: 'cover', display: 'block' }} />
                          : <Box sx={{ width: 48, height: 48, borderRadius: 1, bgcolor: 'grey.100' }} />}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{p.specs?.Title || p.validatedTitle || 'Untitled'}</Typography>
                        {p.sku && <Typography variant="caption" color="text.secondary">{p.sku}</Typography>}
                      </TableCell>
                      <TableCell><Chip size="small" label={channelLabel(p.lastSaleChannel)} sx={{ bgcolor: 'grey.100' }} /></TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{displayDateTime(p.lastSaleAt)}</TableCell>
                      <TableCell align="right">{p.lastSalePrice != null ? formatCurrency(p.lastSalePrice) : '—'}</TableCell>
                      <TableCell align="right">{p.totalSold ?? 0}</TableCell>
                      <TableCell align="right">{p.totalRevenue != null ? formatCurrency(p.totalRevenue) : '—'}</TableCell>
                      <TableCell align="right">{profit != null ? formatCurrency(profit) : '—'}</TableCell>
                    </TableRow>
                  );
                })}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
                      {hasCriteria ? 'No sales match your search or filters.' : 'No sales yet. Sales from connected channels, and items you mark as sold, show up here.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {rows.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Showing 1–{Math.min(limit, rows.length)} of {rows.length} sales
              </Typography>
              {limit < rows.length && (
                <Button size="small" onClick={() => setLimit((n) => n + PAGE_SIZE)} sx={{ textTransform: 'none' }}>Load more</Button>
              )}
            </Box>
          )}
        </>
      )}

      <FilterDialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        fields={filterFields}
        filters={filters}
        emptyFilters={EMPTY_FILTERS}
        onApply={setFilters}
      />

      <SaleHistoryDialog
        open={!!historyProduct}
        onClose={() => setHistoryProduct(null)}
        customer={customer}
        product={historyProduct}
        onChanged={load}
      />
    </Box>
  );
}
